<template>
  <div class="pre-summary">
    <header class="header-title">
      预问诊小结
      <div class="tips">请填写以便医生审核，系统默认带入最近一次用药。</div>
    </header>
    <a-form :model="form" layout="vertical" class="body">
      <a-form-item label="用药状态" v-bind="validateInfos.medicationStatus">
        <a-radio-group
          @change="e => updateRulesByStatus(e.target.value)"
          :options="medicationStatusMap"
          v-model:value="form.medicationStatus"
        />
      </a-form-item>
      <a-form-item class="form-item" label="" v-bind="validateInfos.medicationSituation">
        <div class="label">
          <span>用药清单</span>
          <a-button @click="addMedicine" type="link" size="small">+ 添加药品</a-button>
        </div>
        <div
          class="component medicationSituation"
          v-for="(_, index) in form.medicationSituation"
          :key="index"
        >
          <a-input v-model:value="form.medicationSituation[index]" />
          <a-button @click="removeMedicine(index)" type="link" danger size="small">删除</a-button>
        </div>
      </a-form-item>
      <a-form-item label="用药说明" v-bind="validateInfos.medicationSupplementDescribe">
        <div class="quick-line">
          <div
            @click="addQuickItem(item)"
            v-for="item in quickItems"
            :key="item"
            class="quick-item"
          >
            {{ item }}
          </div>
        </div>
        <a-textarea
          placeholder="请填写其他补充说明"
          v-model:value="form.medicationSupplementDescribe"
          showCount
          :maxlength="200"
        />
      </a-form-item>
    </a-form>
  </div>
</template>

<script setup>
import { Form, message } from 'ant-design-vue'
import { reactive, defineExpose, ref, computed, onMounted, toRaw } from 'vue'
import { medicationStatusMap } from '../config.js'
import { getQueryLastTreatmentPlanForAdvanceConsultationSummary } from '@/api/login.js'
import { useStore } from 'vuex'
const store = useStore()
const useForm = Form.useForm
const info = computed(() => store.state.preMedicalRecord)

const form = reactive({
  medicationStatus: null,
  medicationSituation: [''],
  medicationSupplementDescribe: '',
  // 写死("是否无需填写，true需要填写，false无需填写")
  requiredFillIn: true
})
// 必填项
const rulesRef = reactive({
  medicationStatus: [{ required: true, message: '请选择用药状态' }],
  medicationSituation: [],
  medicationSupplementDescribe: []
})
const quickItems = ref([])
const { clearValidate, validate, validateInfos } = useForm(form, rulesRef)
const addMedicine = () => {
  console.log('添加药品')
  if (form.medicationSituation.length >= 20) {
    message.error('最多添加20项')
  } else {
    form.medicationSituation.push('')
  }
}
const removeMedicine = index => {
  if (form.medicationStatus !== 0 && form.medicationSituation.length <= 1) {
    message.error('最少保留一条')
  } else {
    form.medicationSituation.splice(index, 1)
  }
}
const addQuickItem = item => {
  const len = form.medicationSupplementDescribe.length + item.length
  if (len < 200) {
    form.medicationSupplementDescribe += item
  }
}

// 抛出校验
const submitForm = () => {
  clearValidate()
  return new Promise((resolve, reject) => {
    validate()
      .then(result => {
        resolve({ consultationSummaryDTO: form })
      })
      .catch(err => {
        reject(err)
      })
  })
}

const getFormData = () => {
  return {
    consultationSummaryDTO: {
      ...toRaw(form),
      medicationSituation: [...(form.medicationSituation || [])]
    }
  }
}

const setFormData = data => {
  const draft = data || {}
  form.medicationStatus = draft.medicationStatus ?? null
  const draftSituation = draft.medicationSituation || []
  form.medicationSituation = draftSituation.length ? [...draftSituation] : ['']
  form.medicationSupplementDescribe = draft.medicationSupplementDescribe || ''
  updateRulesByStatus(form.medicationStatus)
}
// 更新 rulesRef 的属性以保持响应性（不要重新赋值 rulesRef）
const updateRulesByStatus = status => {
  clearValidate()
  if (status === 0) {
    // 状态为 0：其他项非必填，清空相应规则
    rulesRef.medicationSituation = []
    rulesRef.medicationSupplementDescribe = []
  } else {
    // 非 0：用药清单与说明为必填（用数组类型校验 medicationSituation）
    rulesRef.medicationSituation = [
      { required: true, message: '请填写用药情况', type: 'array' },
      {
        message: '请填写用药情况',
        type: 'array',
        validator: (_, value) => {
          if (value && value.length > 0 && value.some(item => item.trim() === '')) {
            return Promise.reject()
          }
          return Promise.resolve()
        }
      }
    ]
    rulesRef.medicationSupplementDescribe = [{ required: true, message: '请填写用药说明' }]
  }
}
// isInit 表示是否是组件初始化时调用，初始化时不清空校验信息，避免一进入页面就显示校验错误
const initData = isInit => {
  const params = {
    ucDoctorId: info.value.ucDoctorId,
    ucPatientId: info.value.ucPatientId
  }
  // 获取上一次的用药方案数据
  getQueryLastTreatmentPlanForAdvanceConsultationSummary(params).then(res => {
    const {
      medicationStatus,
      medicationSituation,
      medicationSupplementDescribe,
      medicationSupplementDescribeStr
    } = res
    if (isInit) {
      form.medicationStatus = medicationStatus
      form.medicationSituation =
        medicationSituation && medicationSituation.length > 0 ? medicationSituation : ['']
      form.medicationSupplementDescribe = medicationSupplementDescribeStr || ''
    }
    quickItems.value = medicationSupplementDescribe
    // 根据后端返回的状态初始化校验规则
    updateRulesByStatus(medicationStatus)
  })
}
defineExpose({
  submitForm,
  getFormData,
  setFormData,
  initData
})
</script>

<style lang="scss" scoped>
.pre-summary {
  height: 620px;
  overflow: auto;
  margin-right: 4px;
}
.header-title {
  padding: 12px 20px;
  font-weight: 600;
  border-bottom: 1px solid #f0f0f0;
  .tips {
    display: inline-block;
    font-size: 12px;
    margin-left: 8px;
    font-weight: 500;
    color: #ffa940;
  }
}
.body {
  padding: 20px;
  .form-item {
    .label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }
    .component {
      display: flex;
      align-items: center;
    }
  }
}
.medicationSituation {
  margin-bottom: 12px;
}
.quick-line {
  overflow: auto;
  .quick-item {
    border-radius: 4px;
    border: 1px solid #00000026;
    padding: 8px 16px;
    margin-right: 4px;
    margin-bottom: 4px;
    display: inline-block;
    cursor: pointer;
  }
}
</style>
